import React, { useEffect, useReducer, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Tab, Tabs } from '@fs/zion-ui'
import { useParams, useRouteMatch } from '@fs/zion-router'
import { usePageView } from '@fs/zion-analytics'
import { JobDetails } from './viewDetails/JobDetails'
import { JobViewResults } from './viewResults/JobViewResults'
import { getJob, passThrough } from '../../../../services/toolsService'
import { DatsJobBaseContextProvider } from './DatsJobContext'
import { deepCopy } from '../../../../util/Utils'
import { useQueryParams } from '../../../../util/UrlUtil'
import { ResultsQueriesList } from './downloadResults/ResultsQueriesList'
import { usePageTitle } from '../../../app/PageTitleContext'

export default () => {}

/* eslint-disable no-underscore-dangle */

export function DatsJobBase() {
  usePageView({ page_detail: 'DMConsole:DatsJobBase' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.tools.datsJobDetails'))
  const pathParams = useParams()
  const queryParams = useQueryParams()
  const match = useRouteMatch()
  const [selected, setSelected] = useState(0)
  const { datsJobState, datsJobActions } = useDatsJobBase(pathParams.jobId)

  useEffect(() => {
    datsJobActions.updateJobData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <DatsJobBaseContextProvider value={{ datsJobState, datsJobActions }}>
      <Tabs fullWidth index={selected} onChange={(ind) => setSelected(ind)}>
        <Tab title={t('action.details', 'Details')} to={`${match.url}?tab=details`}>
          {queryParams.get('tab') === 'details' && <JobDetails />}
        </Tab>
        <Tab title={t('dats.resultsQueries', 'Results Queries')} to={`${match.url}?tab=downloadResults`}>
          {queryParams.get('tab') === 'downloadResults' && <ResultsQueriesList />}
        </Tab>
        <Tab title={t('dats.viewResults', 'View Results')} to={`${match.url}?tab=viewResults`}>
          {queryParams.get('tab') === 'viewResults' && <JobViewResults datsJobActions={datsJobActions} />}
        </Tab>
      </Tabs>
    </DatsJobBaseContextProvider>
  )
}

function useDatsJobBase(jobId) {
  const initialState = {
    jobId,
    jobData: null,
    refreshObservers: {},
  }

  const reducer = (_, currentState) => {
    const newState = deepCopy(currentState)
    Object.keys(currentState.refreshObservers).forEach((funcName) => {
      newState.refreshObservers[funcName] = currentState.refreshObservers[funcName]
    })
    return newState
  }

  const [datsJobState, setDatsJobState] = useReducer(reducer, initialState)

  const datsJobActions = {
    addRefreshObserver: (func) => {
      datsJobState.refreshObservers[func.name] = func
      datsJobActions.updateDatsJobState()
      return func.name
    },
    clearRefreshObserver: () => {
      datsJobState.refreshObservers = {}
      datsJobActions.updateDatsJobState()
    },
    notifyRefreshObservers: () => {
      Object.keys(datsJobState.refreshObservers).forEach((funcName) => {
        datsJobState.refreshObservers[funcName]()
      })
    },
    updateJobData: () => {
      return getJob(datsJobState.jobId).then((jobDataResponse) => {
        datsJobState.jobData = jobDataResponse
        datsJobActions.updateDatsJobState()
      })
    },
    actionOnClick: (buttonAction, links) => {
      if (!buttonAction.needsPayload) {
        passThrough(links[buttonAction.linkRel].href, buttonAction.httpMethod, null, {
          'Content-Type': 'application/json',
        }).then(() => {
          datsJobActions.updateJobData()
        })
      }
    },
    updateDatsJobState: () => {
      setDatsJobState(datsJobState)
    },
  }

  return { datsJobState, datsJobActions }
}

/* eslint-enable no-underscore-dangle */
